/*

	The data backend powering a VueList.

	This is essentially a list of "data" (say member ids) that is loaded lazily from the server.

*/
import { Component, Prop, Vue, Watch, toNative } from "vue-facing-decorator";

import { MiaPlaza } from "@/Reinforced.Typings";
import { makeDataProvider, createListStore, type DataProvider, type IListStore } from "./ListStore";

@Component
export class ListStoreRegistration<T> extends Vue {
	// Raw data can be pulled from this URL by replacing PLACEHOLDER_LIMIT and PLACEHOLDER_OFFSET.
	@Prop({ required: false, default: null }) protected dataUrl!: string | DataProvider<T> | null | T[];
	// The pinia store name, i.e., the namespace of this list.
	@Prop({ required: true, type: String }) protected store!: string;
	// Seeded data that has not been pulled through an AJAX call.
	@Prop({ required: false, type: Object, default() {
		return { Items: [], TotalCountEstimate: 4, UnfilteredLimit: 0, UnfilteredOffset: 0 };
	} }) protected initialData!: MiaPlaza.Control.Vue.Stores.Shared.IRangeWithOffset<T>;

	protected created() {
		this.getStore();
	}

	private getStore(): IListStore<T> {
		return createListStore<T>(this.store, this.dataProvider, this.initialData.Items, this.initialData.TotalCountEstimate, this.initialData.UnfilteredOffset + this.initialData.UnfilteredLimit, true);
	}

	@Watch("store")
	protected storeChange(store: string, old: string): void {
		// We cannot change a store name:
		// * We could unregister the existing store and register the new one.
		//	 However, unregistering is tricky since we do not know whether there are
		//	 any pending actions for this store (or other stores referring to it)
		//	 that would then call mutations/action of the store that would lead to
		//	 errors.
		// * We could leave the existing store alone (that's what we do) and
		//	 register the new one. However, typically, you do not only change the
		//	 store name but also the data-url. There's little control over the order
		//	 in which these two change so was the new url meant for the old store or
		//	 only the new one? (We could seemingly fix this by turning the props here
		//	 into a single object that then updates at once. But this puts the burden
		//	 on the caller to make sure that all the inputs into this prop are
		//	 updated in an atomic way as well which is not sustainable.)
		throw Error(`Store name changed from ${old} to ${store}. You cannot change the name of a registered store. Make sure that the store name is bound to the lifetime of the parent.`);
	}

	@Watch("dataProvider")
	protected dataProviderChanged(): void {
		this.getStore().reset(this.dataProvider);
	}

	protected get dataProvider(): DataProvider<T> {
		if (typeof this.dataUrl === "string") {
			return makeDataProvider<T>(this.dataUrl);
		}
		if (Array.isArray(this.dataUrl)) {
			return makeDataProvider<T>(this.dataUrl);
		}
		if (this.dataUrl === null) {
			if (this.initialData.Items.length !== this.initialData.TotalCountEstimate) {
				throw Error(`When no dataUrl has been provided, the initial data must be complete, i.e., TotalCountEstimate must be ${this.initialData.Items.length}`);
			}
			return makeDataProvider<T>(this.initialData.Items);
		}
		return this.dataUrl;
	}

	protected render() {}
}

export default toNative(ListStoreRegistration);
